import { useEffect, useRef, useState, type DragEvent } from 'react';
import { splitMoney, toCents, type Basis, type Granularity } from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { useLocateScroll } from '@/hooks/useLocateScroll';
import type { ActiveMatch, ForecastScope } from './CashFlowWorkspace';
import type { LedgerScope } from './Ledger';
import { formatEtDate, formatEur, pickMoney, type CashFlowEntry, type DayRollup, type MoneyMode } from '@/lib/cashFlow';
import { DateText } from '@/components/ui/DateText';
import { CalendarProvider, type CalendarCtxValue } from './calendar/context';
import { useCalendarModel, type HoverPos, type PlacedEntry } from './calendar/model';
import { DayAgenda, MonthGrid, QuarterGroups, WeekMatrix } from './calendar/layouts';
import { DocHoverCard, GroupHoverCard } from './calendar/HoverCards';

/**
 * Module 1.1 — the secondary "Kalender" lens of the primary view, rendered 1:1
 * with the approved canvas (CashFlowCalendar.tsx). It is fed the SAME finance-core
 * data the table uses and never does monetary arithmetic (L9): the per-day net +
 * running balance come straight from each `DayRollup`, per-period figures from
 * `aggregatePeriods`, an entry's placement from `resolveEntry`, and a split from
 * `toCents` + `splitMoney`. This file is the stateful ORCHESTRATOR — lens,
 * drag/hover/overlay state, the running-balance anchor strip, the drop dialog — and
 * delegates rendering to the granularity layouts (Kuu / Kvartal / Nädal / Päev),
 * each reading the shared {@link CalendarProvider} model. The chosen `granularity`
 * reshapes the calendar; the shared header "Täna" re-centres it on today via the
 * `centerNonce` prop. All module-level chrome (period, filter, money mode, the
 * shared "Ainult maksmata" filter, "Uus kirje") lives in CashFlowView so BOTH lenses
 * share it (L14); only the day-internal `sortBy` ordering is calendar-specific and is
 * passed down from that shared toolbar.
 *
 * A split leaves the remainder as a PERSISTED origin "ghost" (a linked installment
 * still on the original due day, `isGhostEntry`), shown faded with a group tie so
 * the calendar shows where the money used to be due. `sortBy` reorders chips WITHIN
 * a day for presentation only — it never touches any net or balance.
 */
export interface CalendarGridProps {
  days: DayRollup[];
  entries: CashFlowEntry[];
  mode: MoneyMode;
  /** Shared "rõhuta prognoos" toggle — washes forecast chips amber (L14 shared layer). */
  forecastEmphasis?: boolean;
  today: string;
  /** Which calendar shape to render (Päev / Nädal / Kuu / Kvartal). */
  granularity?: Granularity;
  /** Balance before the first in-window day (finance-core), for day-1 opening. */
  openingBalance: number;
  /** The OPENING anchor (algsaldo) the running balance rolls forward from (or none). */
  anchor: { balanceCents: number; asOfDate: string } | null;
  /** Persist a reschedule (move to a new day) — sim-aware in the parent. */
  onReschedule: (id: string, expectedDate: string) => void;
  /** Persist a split (carve a GROSS amount onto a new day) — sim-aware in the parent. */
  onSplit: (id: string, targetDate: string, moveGrossCents: number) => void;
  /** Open the on-demand planner overlay pre-filled for `date` (per-day quick-add). */
  onAddEntry: (date: string) => void;
  /** Day-internal chip ordering, owned by the shared toolbar (calendar-only lens). */
  sortBy?: DaySort;
  /** Bumped by the shared header "Täna" so the viewport re-centres on today. */
  centerNonce?: number;
  /** Drill the opened day into the universal register, scoped to that day — the SAME
   *  scoped `Ledger` the table opens (L14: a number → the register from BOTH lenses). */
  onOpenScope?: (scope: LedgerScope | null) => void;
  /** Open the shared enriched-entry / document detail for ONE entry — the SAME drill
   *  surface the table lens opens (L14: reachable from BOTH lenses, never calendar-only).
   *  Day-click still drills to the register; this powers the per-chip direct-open icon. */
  onOpenEntry?: (id: string) => void;
  /** The shared "locate" target — when set, the calendar scrolls to + flashes the
   *  matching chip (via the shared useLocateScroll hook). Same locate in every lens (L14). */
  activeMatch?: ActiveMatch | null;
  /** The shared reporting basis (Alus): cash placement (due/actual + carry-forward)
   *  or accrual document-date placement. Under accrual the running-balance strip and
   *  pills hide (a bank balance is a cash truth) and drag-to-reschedule is disabled —
   *  moving an expected date would not move the chip there. L14 shared layer. */
  basis?: Basis;
  /** The shared plan-money scope (Prognoos: Väljas / Koos / Lahus). Under 'split'
   *  the closing-balance strip carries the confirmed-only twin ("kinnitatud") next
   *  to the forecast-inclusive close — the calendar's lens-appropriate form of the
   *  matrix's Lõppsaldo sub-line (L14: both lenses show both year-end truths). */
  forecastScope?: ForecastScope;
}

/** Calendar-local chip ordering within a day (never changes net/balance). Exported
 *  so the shared CashFlowView toolbar can own the control (L14: shared chrome). */
export type DaySort = 'date' | 'amount' | 'unpaid';
export const SORT_OPTIONS: { value: DaySort; label: string }[] = [
  { value: 'date', label: 'päev (vaikimisi)' },
  { value: 'unpaid', label: 'maksmata ennem' },
  { value: 'amount', label: 'suurem summa ennem' },
];

/**
 * Smooth-scroll a calendar viewport so today's cell (`[data-now="1"]`) sits in the
 * centre. A no-op when there is no viewport, no today cell, or the host lacks
 * `scrollTo` (e.g. jsdom) — so it is safe to call on mount and from the "Täna"
 * button alike. Exported (pure DOM, no component state) so it is unit-testable.
 */
export function centerOnToday(root: HTMLElement | null): void {
  if (!root || typeof root.scrollTo !== 'function') return;
  const el = root.querySelector<HTMLElement>('[data-now="1"]');
  if (!el) return;
  root.scrollTo({
    left: Math.max(0, el.offsetLeft - root.clientWidth / 2 + el.clientWidth / 2),
    top: Math.max(0, el.offsetTop - root.clientHeight / 2 + el.clientHeight / 2),
    behavior: 'smooth',
  });
}

export function CalendarGrid({
  days,
  entries,
  mode,
  forecastEmphasis = false,
  today,
  granularity = 'month',
  openingBalance,
  anchor,
  onReschedule,
  onSplit,
  onAddEntry,
  sortBy = 'date',
  centerNonce = 0,
  onOpenScope,
  onOpenEntry,
  activeMatch = null,
  basis = 'cashBasis',
  forecastScope = 'split',
}: CalendarGridProps) {
  const accrual = basis === 'accrualBasis';
  // Drag-to-reschedule cues: the entry being dragged (id + its origin day, so the
  // drop is a no-op back on the same date) + the day currently hovered over.
  const [drag, setDrag] = useState<CashFlowEntry | null>(null);
  const [overDate, setOverDate] = useState<string | null>(null);
  // A pending drop on a different day → the reschedule-or-split dialog.
  const [dropTarget, setDropTarget] = useState<{ entry: CashFlowEntry; to: string } | null>(null);
  // Floating hover cards: an installment (group) card and a single-document card.
  const [groupHover, setGroupHover] = useState<HoverPos | null>(null);
  const [docHover, setDocHover] = useState<HoverPos | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const model = useCalendarModel(days, entries, today, openingBalance, basis);

  // Centre the scroll viewport on today's cell — on mount and whenever the shape
  // changes (the "Täna" button calls the same path). centerOnToday feature-detects
  // scrollTo so jsdom (no smooth scroll) is a no-op rather than a crash.
  const scrollToToday = () => centerOnToday(scrollRef.current);
  useEffect(() => {
    const id = window.setTimeout(scrollToToday, 60);
    return () => window.clearTimeout(id);
  }, [granularity, centerNonce]);

  // Reveal a "locate" target chip (scroll + flash) via the SAME shared hook the table
  // and register use, so Cmd+K / deep-link / "Leia" land identically in every lens (L14).
  useLocateScroll(scrollRef, activeMatch);

  // Order ONE day's entries for presentation (calendar-only lens). Never feeds the
  // running balance — the day net uses every entry regardless of this ordering.
  const arrangeDay = (list: PlacedEntry[]): PlacedEntry[] => {
    let out = list;
    if (sortBy === 'amount') {
      out = [...out].sort((a, b) => pickMoney(b.e.money, mode) - pickMoney(a.e.money, mode));
    } else if (sortBy === 'unpaid') {
      const rank = (p: PlacedEntry) => (p.e.actualDate === null ? 0 : 1);
      out = [...out].sort((a, b) => rank(a) - rank(b) || pickMoney(b.e.money, mode) - pickMoney(a.e.money, mode));
    }
    return out;
  };

  // Drop a dragged chip on a day → open the reschedule-or-split dialog for that day.
  // Dropping a chip back on its own expected date (or with nothing held) is a no-op.
  const handleDrop = (date: string) => {
    const held = drag;
    setDrag(null);
    setOverDate(null);
    if (held === null || held.expectedDate === date) return;
    setDropTarget({ entry: held, to: date });
  };

  if (!model) return null;

  const closingBalance = model.closingBalance;

  const ctxValue: CalendarCtxValue = {
    mode,
    forecastEmphasis,
    accrual,
    today,
    model,
    arrangeDay,
    drag,
    overDate,
    // Under accrual a reschedule would NOT move the chip (placement is the document
    // date), so the drag never starts — CalendarChip gates it at the source
    // (draggable=false + onDragStart preventDefault); the reschedule/split function
    // itself stays reachable from the register + document detail in both bases (L14).
    startDrag: (e) => setDrag(e),
    endDrag: () => {
      setDrag(null);
      setOverDate(null);
    },
    dayDragOver: (date: string, ev: DragEvent) => {
      if (drag === null) return;
      ev.preventDefault();
      setOverDate(date);
    },
    dayDragLeave: (date: string) => setOverDate((x) => (x === date ? null : x)),
    dayDrop: (date: string, ev: DragEvent) => {
      ev.preventDefault();
      handleDrop(date);
    },
    showGroupHover: setGroupHover,
    showDocHover: setDocHover,
    // A day click drills STRAIGHT into the universal register, scoped to that day —
    // the same single-step "number → scoped Ledger" path the table uses (L14). There
    // is no intermediate day overlay; the day's expected-vs-actual breakdown now lives
    // in the register's scope header. `net` is computed by the cell for display only
    // and is intentionally ignored here (the register reads its own finance-core sums).
    openDay: (date) => onOpenScope?.({ label: `Päev · ${formatEtDate(date)}`, match: (row) => row.tahtaeg === date }),
    // A carried-forward summary group drills into the SAME universal register the
    // table opens (L14), scoped to the EXACT folded ids finance-core returned — not
    // a date predicate, so a partial/priority-excluded set stays exact (F1).
    openCarriedForward: (kind, ids) => {
      const set = new Set(ids);
      onOpenScope?.({
        label: kind === 'income' ? 'Üle tähtaja · laekumised' : 'Üle tähtaja · maksed',
        match: (row) => set.has(row.e.id),
      });
    },
    // The per-document direct-open affordance — opens the SAME enriched-entry / document
    // detail the table lens opens (L14: never calendar-only). Day-click still drills to the
    // register (above); this is the chip-level shortcut to ONE document.
    openEntry: (id) => onOpenEntry?.(id),
    addEntry: onAddEntry,
  };

  return (
    <div className="flex h-full flex-col" data-testid="calendar-grid">
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
        <CalendarProvider value={ctxValue}>
          {granularity === 'day' ? (
            <DayAgenda />
          ) : granularity === 'week' ? (
            <WeekMatrix />
          ) : granularity === 'quarter' ? (
            <QuarterGroups />
          ) : (
            <MonthGrid />
          )}
        </CalendarProvider>
      </div>

      {/* The forecast closing balance is a CASH truth (the running balance
          rolled forward from the opening anchor) — under the accrual basis it
          cannot follow from the grid above it, so the strip hides (same rule
          as the table's saldo rows). */}
      {!accrual && (
        <div className="mt-2 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-2 text-xs">
          {/* The confirmed-only twin ("Lahus" scope): the SAME finance-core
              confirmedRunningBalance the matrix's Lõppsaldo sub-line reads —
              shown only when plan money makes the two closes differ (L14). */}
          {forecastScope === 'split' && model.confirmedClosingBalance !== closingBalance && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-muted-foreground">Kinnitatud lõppsaldo</span>
              <span className="tabular-nums text-sm font-medium text-muted-foreground">
                {formatEur(model.confirmedClosingBalance)}
              </span>
            </div>
          )}
          <div className={cn('flex items-center gap-2', forecastScope === 'split' && model.confirmedClosingBalance !== closingBalance ? '' : 'ml-auto')}>
            <span className="text-muted-foreground">Prognoositud lõppsaldo</span>
            <span
              className={cn(
                'tabular-nums text-sm font-semibold',
                closingBalance >= (anchor?.balanceCents ?? 0) ? 'text-[hsl(var(--income))]' : 'text-foreground',
              )}
            >
              {formatEur(closingBalance)}
            </span>
          </div>
        </div>
      )}

      {dropTarget && (
        <DropSplitDialog
          entry={dropTarget.entry}
          targetDate={dropTarget.to}
          onReschedule={onReschedule}
          onSplit={onSplit}
          onClose={() => setDropTarget(null)}
        />
      )}

      {groupHover && <GroupHoverCard pos={groupHover} entries={entries} today={today} />}
      {docHover && <DocHoverCard pos={docHover} entries={entries} today={today} mode={mode} />}
    </div>
  );
}

interface DropSplitDialogProps {
  entry: CashFlowEntry;
  targetDate: string;
  onReschedule: (id: string, expectedDate: string) => void;
  onSplit: (id: string, targetDate: string, moveGrossCents: number) => void;
  onClose: () => void;
}

/**
 * The drop dialog: move the WHOLE entry to `targetDate` (reschedule) or carve a GROSS
 * amount off onto it (split). The euro input is converted to cents with finance-core's
 * `toCents`; `splitMoney` previews the moved/remainder breakdown. The split is only
 * offered for a strictly-partial amount (0 < moved < gross) — moving everything is a
 * reschedule, moving nothing is a no-op. No monetary maths happens here.
 */
function DropSplitDialog({ entry, targetDate, onReschedule, onSplit, onClose }: DropSplitDialogProps) {
  const [amount, setAmount] = useState('');
  const gross = entry.money.gross;

  // Parse the euro input to cents, tolerating an empty/garbled value (no throw).
  const parsed = Number.parseFloat(amount.replace(',', '.'));
  const moveCents = Number.isFinite(parsed) ? toCents(parsed) : null;
  const canSplit = moveCents !== null && moveCents > 0 && moveCents < gross;
  const preview = canSplit ? splitMoney(entry.money, moveCents) : null;

  return (
    <div
      role="dialog"
      aria-label="Tõsta või poolita kirje"
      data-testid="drop-split-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-foreground">Tõsta kirje uuele päevale</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          <span className="text-foreground/80">{entry.party}</span> · kogusumma{' '}
          <span className="tabular-nums">{formatEur(gross)}</span> → <DateText iso={targetDate} />
        </p>

        <button
          type="button"
          onClick={() => {
            onReschedule(entry.id, targetDate);
            onClose();
          }}
          className="mt-3 w-full rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground hover:bg-accent"
        >
          Tõsta kogu summa siia
        </button>

        <div className="mt-3 border-t border-border pt-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>…või poolita: summa, mille siia tõstad (€)</span>
            <input
              type="text"
              inputMode="decimal"
              aria-label="Tõstetav summa eurodes"
              value={amount}
              onChange={(ev) => setAmount(ev.target.value)}
              placeholder="nt 500"
              className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
            />
          </label>
          {preview && (
            <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid="split-preview">
              Jääb <span className="tabular-nums text-foreground/80">{formatEur(preview.remainder.gross)}</span> ·
              liigub <span className="tabular-nums text-foreground/80">{formatEur(preview.moved.gross)}</span>
            </p>
          )}
          <button
            type="button"
            disabled={!canSplit}
            onClick={() => {
              // The button is only enabled when `canSplit`, which guarantees a
              // positive sub-total cents value (`moveCents`).
              onSplit(entry.id, targetDate, moveCents!);
              onClose();
            }}
            className="mt-2 w-full rounded-md border border-primary/50 bg-primary/15 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Poolita
          </button>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full text-center text-[11px] text-muted-foreground hover:text-foreground"
        >
          Tühista
        </button>
      </div>
    </div>
  );
}
